import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';

// Pages
import LoginPage from './pages/LoginPage';
import VerifyPage from './pages/VerifyPage';
import WelcomePage from './pages/WelcomePage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import DashboardPage from './pages/DashboardPage';
import PhaseDetailPage from './pages/PhaseDetailPage';
import ContentReviewPage from './pages/ContentReviewPage';
import BlogCalendarReviewPage from './pages/BlogCalendarReviewPage';
import ActivationPage from './pages/ActivationPage';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminClientDetailPage from './pages/admin/AdminClientDetailPage';
import AdminClientsPage from './pages/admin/AdminClientsPage';
import AdminCoachInboxPage from './pages/admin/AdminCoachInboxPage';
import AdminTeamPage from './pages/admin/AdminTeamPage';

// Layout
import AuthLayout from './components/layout/AuthLayout';
import AppLayout from './components/layout/AppLayout';
import AdminLayout from './components/layout/AdminLayout';

// Helper to check if profile is complete
function isProfileComplete(user: any) {
  if (!user) return false;
  // Admins and team members always pass through
  if (user.is_admin || user.role === 'admin' || user.role === 'team') return true;
  // For clients, just need a business name to get started
  return !!user.business_name;
}

function App() {
  const { isLoading, isAuthenticated, user, loadUser } = useAuthStore();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Profile completeness check
  const profileComplete = isAuthenticated && isProfileComplete(user);

  return (
    <Routes>
      {/* Public routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/verify" element={<VerifyPage />} />
      </Route>

      {/* Onboarding routes (protected but no layout) */}
      {isAuthenticated && !profileComplete && (
        <>
          <Route path="/onboarding/welcome" element={<WelcomePage />} />
          <Route path="/onboarding/setup" element={<ProfileSetupPage />} />
          <Route path="*" element={<Navigate to="/onboarding/setup" replace />} />
        </>
      )}

      {/* Admin routes */}
      {isAuthenticated && profileComplete && user?.is_admin && (
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/clients/:id" element={<AdminClientDetailPage />} />
          <Route path="/admin/clients" element={<AdminClientsPage />} />
          <Route path="/admin/coach" element={<AdminCoachInboxPage />} />
          <Route path="/admin/team" element={<AdminTeamPage />} />
        </Route>
      )}

      {/* Protected app routes (for users with complete profiles) */}
      {isAuthenticated && profileComplete && (
        <Route element={<AppLayout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/phases/:slug" element={<PhaseDetailPage />} />
          <Route path="/phases/blog/review" element={<BlogCalendarReviewPage />} />
          <Route path="/phases/:slug/review" element={<ContentReviewPage />} />
          <Route path="/activation" element={<ActivationPage />} />
          <Route path="/launch" element={<ActivationPage />} />
        </Route>
      )}

      {/* Redirect non-authenticated users to login */}
      {!isAuthenticated && <Route path="*" element={<Navigate to="/login" replace />} />}

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
